import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';

const UPCOMING_STATUSES = ['window_reserved', 'en_route', 'artist_ready', 'in_service', 'handoff_in_progress'];
const PAST_STATUSES = ['completed', 'cancelled', 'window_expired'];

const statusConfig = {
  window_reserved: { label: 'WINDOW OPEN', color: 'var(--orange)' },
  en_route:        { label: 'EN ROUTE',    color: 'var(--orange)' },
  artist_ready:    { label: 'ARTIST READY', color: 'var(--orange)' },
  in_service:      { label: 'IN SERVICE',  color: 'var(--orange)' },
  handoff_in_progress: { label: 'HANDOFF', color: 'var(--orange)' },
  completed:       { label: 'COMPLETED',   color: '#2ecc71' },
  cancelled:       { label: 'CANCELLED',   color: '#e74c3c' },
  window_expired:  { label: 'EXPIRED',     color: '#e74c3c' },
  scheduled:       { label: 'SCHEDULED',   color: 'var(--text-muted)' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { label: status, color: 'var(--text-muted)' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        color: '#fff',
        backgroundColor: cfg.color,
        textTransform: 'uppercase',
      }}
    >
      {cfg.label}
    </span>
  );
}

function WindowCard({ booking, onCancel, isPast, t }) {
  const isActive = booking.status === 'window_reserved';
  const canCancel = !isPast && !['cancelled', 'in_service', 'handoff_in_progress'].includes(booking.status);

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        borderRadius: 16,
        padding: 24,
        border: isActive ? '2px solid var(--orange)' : '1px solid var(--border)',
        opacity: isPast ? 0.55 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
    >
      {/* Date + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>
            {new Date(booking.booking_date).toLocaleDateString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric',
            })}
          </span>
        </div>
        <StatusBadge status={booking.status} />
      </div>

      {/* Time window */}
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.5 }}>
        {booking.window_start} &ndash; {booking.window_end}
      </div>

      {/* Artist */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {booking.artist_avatar ? (
          <img
            src={booking.artist_avatar}
            alt={booking.artist_name}
            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--orange)',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {(booking.artist_name || '?')[0].toUpperCase()}
          </div>
        )}
        <span style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>
          {booking.artist_name}
        </span>
      </div>

      {/* Service + price */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--border)',
          paddingTop: 12,
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{booking.service_names}</span>
        <span style={{ color: 'var(--orange)', fontWeight: 700, fontSize: 15 }}>
          {booking.total_price} EGP
        </span>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        {isActive && (
          <Link
            to={`/premium/on-my-way/${booking.id}`}
            style={{
              flex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '10px 0',
              borderRadius: 10,
              backgroundColor: 'var(--orange)',
              color: '#000',
              fontWeight: 700,
              fontSize: 13,
              textDecoration: 'none',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {t('premium.onMyWay', "I'M ON MY WAY")}
          </Link>
        )}
        {canCancel && (
          <button
            onClick={() => onCancel(booking.id)}
            style={{
              flex: isActive ? 'none' : 1,
              padding: '10px 18px',
              borderRadius: 10,
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'border-color 0.2s, color 0.2s',
            }}
          >
            {t('premium.cancel', 'Cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MyWindows() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getPremiumBookings();
      setBookings(res.data || res);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleCancel = async (id) => {
    if (!window.confirm(t('premium.confirmCancel', 'Cancel this window booking?'))) return;
    try {
      await api.cancelPremiumBooking(id);
      setBookings((prev) =>
        prev.map((b) => (b._id === id ? { ...b, status: 'cancelled' } : b))
      );
    } catch (err) {
      alert(err.message || 'Failed to cancel booking');
    }
  };

  const filtered = bookings.filter((b) => {
    if (activeTab === 'upcoming') return UPCOMING_STATUSES.includes(b.status);
    return PAST_STATUSES.includes(b.status);
  });

  const tabStyle = (isActive) => ({
    padding: '8px 28px',
    borderRadius: 30,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    backgroundColor: isActive ? 'var(--orange)' : 'transparent',
    color: isActive ? '#000' : 'var(--text-muted)',
    transition: 'all 0.25s',
  });

  return (
    <div style={{ padding: '7rem 1.5rem 5rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 32,
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <h1
            style={{
              color: 'var(--text-primary)',
              fontSize: 30,
              fontWeight: 800,
              margin: 0,
              letterSpacing: -0.5,
            }}
          >
            {t('premium.myWindows', 'My Windows')}
          </h1>
          <Link
            to="/premium/book"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 24px',
              borderRadius: 10,
              backgroundColor: 'var(--orange)',
              color: '#000',
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none',
              letterSpacing: 0.3,
            }}
          >
            + {t('premium.newWindow', 'New Window')}
          </Link>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'inline-flex',
            backgroundColor: 'var(--bg-card)',
            borderRadius: 30,
            padding: 4,
            marginBottom: 32,
          }}
        >
          <button onClick={() => setActiveTab('upcoming')} style={tabStyle(activeTab === 'upcoming')}>
            {t('premium.upcoming', 'Upcoming')}
          </button>
          <button onClick={() => setActiveTab('past')} style={tabStyle(activeTab === 'past')}>
            {t('premium.past', 'Past')}
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            <div
              style={{
                width: 36,
                height: 36,
                border: '3px solid var(--border)',
                borderTopColor: 'var(--orange)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 16px',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{ fontSize: 14 }}>{t('common.loading', 'Loading...')}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ textAlign: 'center', padding: 40, color: '#e74c3c', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 15 }}>
            {activeTab === 'upcoming'
              ? t('premium.noUpcoming', 'No upcoming window bookings')
              : t('premium.noPast', 'No past window bookings')}
          </div>
        )}

        {/* Grid */}
        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 20,
            }}
          >
            {filtered.map((booking) => (
              <WindowCard
                key={booking.id}
                booking={booking}
                onCancel={handleCancel}
                isPast={activeTab === 'past'}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
